/* eslint-disable react/prop-types */
"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";
import EventToolsBackButton from "./EventToolsBackButton";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";

const WaitlistModal = ({
  visible = false,
  onClose,
  entries = [],
  isInWaitlist = false,
  myPosition = null,
  waitlistCount = 0,
  availabilityUnalertedReason = null,
  prepayRequired = false,
  canJoin = false,
  manualJoinAvailable = false,
  manualJoinFreeSlots = 0,
  joinError = "",
  entryReason = "",
  readOnly = false,
  entriesHidden = false,
  canHostPromote = false,
  hostPromotingUserId = null,
  loading = false,
  loadError = "",
  submitting = false,
  openedFromAvailabilityAlert = false,
  onJoin,
  onManualJoin,
  onLeave,
  onHostPromote,
  onOpenProfile,
  onRetry,
  onBack = null,
  embedded = false,
}) => {
  const { t } = useTranslation("events");

  if (!visible) return null;

  const canSubmitJoin = !readOnly && !loading && !submitting && canJoin;
  const canSubmitLeave = !readOnly && !loading && !submitting && isInWaitlist;
  const showManualJoin =
    !readOnly &&
    isInWaitlist &&
    manualJoinAvailable &&
    typeof onManualJoin === "function";
  const canSubmitManualJoin =
    showManualJoin &&
    !loading &&
    !submitting;
  const isPrivateParticipantView = Boolean(entriesHidden && !readOnly);
  const showAvailabilityLegend = Boolean(
    readOnly && entries.some((entry) => entry.availability_alerted_in_current_round)
  );
  const showGenderCapacityReason = Boolean(
    showAvailabilityLegend &&
      availabilityUnalertedReason === "gender_capacity_full" &&
      entries.some((entry) => !entry.availability_alerted_in_current_round)
  );
  const canPromoteFromHostView = Boolean(readOnly && canHostPromote && onHostPromote);
  const showSpotAlreadyTaken = Boolean(
    openedFromAvailabilityAlert &&
      isInWaitlist &&
      !manualJoinAvailable &&
      !loading &&
      !loadError
  );
  const isCapacityEntryPrompt = Boolean(
    !readOnly && !isInWaitlist && entryReason
  );
  const subtitle = isCapacityEntryPrompt
    ? ""
    : manualJoinAvailable && prepayRequired && !readOnly
    ? t("events.details.waitlist.prepay_claim_hint", {
        slots: Math.max(1, Number(manualJoinFreeSlots || 0)),
      })
    : prepayRequired && readOnly
    ? t("events.details.waitlist.prepay_host_hint")
    : prepayRequired
    ? t("events.details.waitlist.prepay_hint")
    : manualJoinAvailable && !readOnly
    ? t("events.details.waitlist.manual_join_hint", {
        slots: Math.max(1, Number(manualJoinFreeSlots || 0)),
      })
    : isPrivateParticipantView
    ? t("events.details.waitlist.private_hint")
    : readOnly
    ? t("events.details.waitlist.host_hint")
    : t("events.details.waitlist.autojoin_hint");

  const modalFooter = loadError ? (
    <div className="flex gap-3">
      <AppModalButton
        onClick={onRetry}
        disabled={loading || typeof onRetry !== "function"}
        loading={loading}
        className="flex-1 text-[#2563EB]"
      >
        {t("events.details.waitlist.retry")}
      </AppModalButton>
      <AppModalButton variant="secondary" onClick={onClose} className="flex-1">
        {t("events.details.waitlist.close")}
      </AppModalButton>
    </div>
  ) : (
    <div className={`flex gap-3 ${showManualJoin ? "flex-wrap" : ""}`}>
      {!readOnly &&
        (isInWaitlist ? (
          showManualJoin ? (
            <>
              <AppModalButton
                onClick={onManualJoin}
                disabled={!canSubmitManualJoin}
                loading={submitting}
                className="min-w-[140px] flex-1 text-[#2563EB]"
              >
                {submitting
                  ? t("events.details.waitlist.loading")
                  : prepayRequired
                  ? t("events.details.waitlist.claim_and_pay")
                  : t("events.details.waitlist.join_now")}
              </AppModalButton>
              <AppModalButton
                variant="danger"
                onClick={onLeave}
                disabled={!canSubmitLeave}
                loading={submitting}
                className="min-w-[140px] flex-1"
              >
                {submitting
                  ? t("events.details.waitlist.loading")
                  : prepayRequired
                  ? t("events.details.waitlist.stop_waiting")
                  : t("events.details.waitlist.leave_waitlist")}
              </AppModalButton>
            </>
          ) : (
            <AppModalButton
              variant="danger"
              onClick={onLeave}
              disabled={!canSubmitLeave}
              loading={submitting}
              className="flex-1"
            >
              {submitting
                ? t("events.details.waitlist.loading")
                : prepayRequired
                ? t("events.details.waitlist.stop_waiting")
                : t("events.details.waitlist.leave_waitlist")}
            </AppModalButton>
          )
        ) : (
          <AppModalButton
            onClick={onJoin}
            disabled={!canSubmitJoin}
            loading={submitting}
            className="flex-1 text-[#2563EB]"
          >
            {submitting
              ? t("events.details.waitlist.loading")
              : isCapacityEntryPrompt
              ? t("events.details.waitlist.join_waitlist")
              : t("events.details.waitlist.join_button_short")}
          </AppModalButton>
        ))}
      <AppModalButton
        variant="secondary"
        onClick={onClose}
        className={`${
          readOnly
            ? "w-full"
            : showManualJoin
            ? "w-full"
            : "flex-1"
        }`}
      >
        {t("events.details.waitlist.close")}
      </AppModalButton>
    </div>
  );

  const bodyContent = (
    <div>
      {loading ? (
        <div
          className="flex min-h-[150px] flex-col items-center justify-center gap-4 px-4 py-8 text-center text-sm font-semibold text-white/80"
          role="status"
        >
          <span className="h-8 w-8 animate-spin rounded-full border-2 border-white/25 border-t-white" />
          <span>{t("events.details.waitlist.loading_status")}</span>
        </div>
      ) : loadError ? (
        <div className="rounded-2xl border border-red-200/25 bg-red-300/10 px-4 py-5 text-center text-sm font-semibold leading-6 text-red-100">
          {loadError}
        </div>
      ) : (
        <>
      {prepayRequired && !isCapacityEntryPrompt ? (
        <div className="mb-3 rounded-2xl border border-white/20 bg-white/12 px-4 py-4 text-center text-base font-extrabold text-white shadow-[0_12px_28px_rgba(7,15,38,0.18)]">
          {t("events.details.waitlist.waiting_count", {
            count: Math.max(0, Number(waitlistCount || 0)),
          })}
        </div>
      ) : !readOnly && isInWaitlist ? (
        <div className="mb-3 rounded-2xl border border-white/20 bg-white/12 px-4 py-4 text-center text-base font-extrabold text-white shadow-[0_12px_28px_rgba(7,15,38,0.18)]">
          {myPosition
            ? t("events.details.waitlist.your_position", { position: myPosition })
            : t("events.details.waitlist.position_pending")}
        </div>
      ) : null}

      {showSpotAlreadyTaken ? (
        <div className="mb-3 rounded-2xl border border-amber-200/30 bg-amber-300/12 px-4 py-4 text-center text-sm font-semibold leading-6 text-amber-50">
          {t("events.details.waitlist.spot_already_taken")}
        </div>
      ) : null}

      {!readOnly && !isInWaitlist && entryReason ? (
        <div className="mb-3 rounded-2xl border border-amber-200/30 bg-amber-300/12 px-4 py-4 text-center text-sm font-semibold leading-6 text-amber-50">
          {entryReason}
        </div>
      ) : null}

      {isCapacityEntryPrompt || isPrivateParticipantView ? null : entries.length === 0 ? (
        isInWaitlist ? null : (
          <div className="px-2 py-3 text-sm text-white/80 text-center">
            {t("events.details.waitlist.empty")}
          </div>
        )
      ) : (
        <div className="overflow-x-auto">
          <div className="flex min-w-max flex-nowrap gap-2 px-2 py-2">
            {entries.map((entry) => {
              const avatarSrc = resolveUserAvatarSrc(
                entry,
                "/assets/events/default_avatar.png"
              );
              const canOpenProfile =
                typeof onOpenProfile === "function" && Boolean(entry.code);
              const alertedThisRound = Boolean(
                readOnly && entry.availability_alerted_in_current_round
              );
              const avatarRingClass = alertedThisRound
                ? "ring-[3px] ring-emerald-400 ring-offset-2 ring-offset-transparent"
                : entry.avatar_custom_url
                ? "ring-2 ring-white/80"
                : "";
              const alertTitle = alertedThisRound
                ? t("events.details.waitlist.alerted_current_round")
                : undefined;

              return (
                <div
                  key={`${entry.user_id}-${entry.position ?? "waiting"}`}
                  className="flex-shrink-0 flex flex-col items-center"
                >
                  {!prepayRequired ? (
                    <span className="text-sm text-white font-extrabold">
                      #{entry.position}
                    </span>
                  ) : null}
                  {canOpenProfile ? (
                    <button
                      type="button"
                      onClick={() => onOpenProfile(entry.code)}
                      className="flex flex-col items-center"
                    >
                      <img
                        src={avatarSrc}
                        alt="avatar"
                        title={alertTitle}
                        className={`rounded-full w-[55px] h-[55px] object-cover ${avatarRingClass}`}
                      />
                      <span className="text-lg text-white font-extrabold">
                        {entry.code}
                      </span>
                    </button>
                  ) : (
                    <>
                      <img
                        src={avatarSrc}
                        alt="avatar"
                        title={alertTitle}
                        className={`rounded-full w-[55px] h-[55px] object-cover ${avatarRingClass}`}
                      />
                      <span className="text-lg text-white font-extrabold">
                        {entry.code || "—"}
                      </span>
                    </>
                  )}
                  {canPromoteFromHostView ? (
                    <button
                      type="button"
                      disabled={Boolean(loading || submitting || hostPromotingUserId === entry.user_id)}
                      onClick={() => onHostPromote?.(entry.user_id)}
                      className={`mt-2 px-3 h-8 rounded-lg text-sm font-semibold ${
                        hostPromotingUserId === entry.user_id
                          ? "bg-white/35 text-white/90 cursor-not-allowed"
                          : "bg-white text-blue-600"
                      }`}
                    >
                      {hostPromotingUserId === entry.user_id
                        ? t("events.details.waitlist.promoting")
                        : t("events.details.waitlist.add_to_event")}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showAvailabilityLegend ? (
        <div className="mt-3 space-y-2 px-1 text-[13px] font-medium leading-5 text-white/75">
          <div className="flex items-start gap-2">
            <span aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 rounded-full border-[3px] border-emerald-400" />
            <span>{t("events.details.waitlist.alerted_legend")}</span>
          </div>
          {showGenderCapacityReason ? (
            <p className="pl-6">{t("events.details.waitlist.gender_capacity_unalerted_reason")}</p>
          ) : null}
        </div>
      ) : null}

      {!readOnly && !isInWaitlist && joinError ? (
        <div className="mt-3 text-sm text-red-200">{joinError}</div>
      ) : null}
        </>
      )}
    </div>
  );

  if (embedded) {
    return (
      <>
        {subtitle ? (
          <p className="mb-5 text-center text-[15px] font-medium leading-7 text-white/88">
            {subtitle}
          </p>
        ) : null}
        {bodyContent}
        <div className="mt-5">{modalFooter}</div>
      </>
    );
  }

  return (
    <AppModalShell
      open={visible}
      title={t(
        isCapacityEntryPrompt
          ? "events.details.waitlist.title"
          : prepayRequired
          ? "events.details.waitlist.prepay_title"
          : "events.details.waitlist.title"
      )}
      description={subtitle || undefined}
      onClose={onClose}
      closeAriaLabel={t("events.details.waitlist.close")}
      zIndexClassName="z-[1200]"
      maxWidthClassName="max-w-[560px]"
      footer={modalFooter}
      descriptionClassName="text-center"
      leftAction={
        typeof onBack === "function" ? (
          <EventToolsBackButton
            onClick={onBack}
            label={t("events.details.more_actions.back")}
          />
        ) : null
      }
    >
      {bodyContent}
    </AppModalShell>
  );
};

export default WaitlistModal;
